import { createSignal, type JSX, Show } from 'solid-js';
import { type PlanStartInput, type PlanStartOutcome, startPlan } from '../lib/plan-start';
import { useRepoList } from '../lib/repos';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';
import { StartPlanForm } from './StartPlanForm';
import { StartPlanResult } from './StartPlanResult';

// The scout-start guts (arc 101 slice 07). Container — resolves the repo list
// the picker offers, owns the POST, and hands the verdict to
// <StartPlanResult/>. Mounted only while the dialog is open (<StartPlanModal/>
// is the shell), so the repo walk starts when a human asks to start a plan.
//
// The repo list is the SHARED `useRepoList` (`lib/repos.ts`), never a second
// fetch: `/v1/repos` advertises no filter, so the exhaust walk is the only
// correct read of "the account's enabled repos", and its cache is warm from any
// other surface that already asked.
//
// LOADING AND FAILED ARE NOT EMPTY. The picker's entire vocabulary is that walk,
// and the form's "no enabled repos yet" line is a claim ABOUT THE ACCOUNT — so a
// list still in flight, or one whose read failed outright, must not render it:
// both would tell a maintainer their account has nothing to plan against and send
// them off to enable a repo that is already enabled. The three states go through
// <QueryBoundary/> over the paged view (`loading/error only while nothing is on
// screen`): a skeleton while page one is in flight, the retryable <ErrorState/>
// when it failed, and the form ONLY once the walk landed. A walk that lands EMPTY
// still reads as empty — that is the one case the sentence is true. Same rule the
// donate wizard's box step holds, for the same reason.
//
// NOTHING RE-READS THE EPICS INDEX, and that is deliberate. A start mints a
// scout TASK; the plan this screen lists is authored minutes later, when the box
// emits `plan_authored` (`apps/worker/src/fleet/scout-plan-ingest.ts`).
// Refetching here would ask the server to confirm that nothing happened — the
// rule <NewTaskModal/> holds for an escalated ask — so the result points at the
// queue board instead, the one surface that can show the new work today.

export type StartPlanDialogProps = {
  readonly token: string | null;
  readonly onClose: () => void;
};

export function StartPlanDialog(props: StartPlanDialogProps): JSX.Element {
  const repos = useRepoList(props.token);
  const [pending, setPending] = createSignal(false);
  const [outcome, setOutcome] = createSignal<PlanStartOutcome | null>(null);

  const submit = async (input: PlanStartInput): Promise<void> => {
    setPending(true);
    setOutcome(await startPlan(input, props.token));
    setPending(false);
  };

  return (
    <Show
      when={outcome()}
      fallback={
        <QueryBoundary query={repos.view} loading={<Skeleton height="12rem" />}>
          {(rows) => (
            <StartPlanForm
              repos={rows}
              pending={pending()}
              onSubmit={(input) => void submit(input)}
              onCancel={props.onClose}
            />
          )}
        </QueryBoundary>
      }
    >
      {(result) => (
        <StartPlanResult
          outcome={result()}
          onAgain={() => setOutcome(null)}
          onClose={props.onClose}
        />
      )}
    </Show>
  );
}
